import ArchiveIcon from '@mui/icons-material/Archive';
import CircleIcon from '@mui/icons-material/Circle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InventoryIcon from '@mui/icons-material/Inventory';
import MarkChatUnread from '@mui/icons-material/MarkChatUnread';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArchiveAlertsMutation, useReadAlertsMutation } from 'services/api/alerts.api';
import type { Alert } from 'services/types/alert';

/* Inspired by https://mui.com/store/items/mantis-free-react-admin-dashboard-template/ */
interface AlertsProps {
  alerts: Alert[];
  refetchAlerts: () => Promise<unknown>;
  /** When true, hides the bell icon button — the popover is controlled externally. */
  externalOpen?: boolean;
  externalAnchorEl?: HTMLElement | null;
  onExternalClose?: () => void;
}

const Alerts = ({ alerts, refetchAlerts, externalOpen, externalAnchorEl, onExternalClose }: AlertsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const [readAlerts] = useReadAlertsMutation();
  const [archiveAlerts] = useArchiveAlertsMutation();

  const read = alerts?.filter((a) => a.readAt === null).length || 0;
  const isExternal = externalOpen !== undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (isExternal) {
      onExternalClose?.();
    } else {
      setAnchorEl(null);
    }
  };

  const open = isExternal ? !!externalOpen : Boolean(anchorEl);
  const resolvedAnchorEl = isExternal ? externalAnchorEl : anchorEl;

  const alertsByBusinessUnit = alerts?.reduce(
    (acc, alert) => {
      const businessUnit = alert.businessUnit?.name || '';
      if (!acc[businessUnit]) {
        acc[businessUnit] = [];
      }
      acc[businessUnit].push(alert);
      return acc;
    },
    {} as Record<string, Alert[]>
  );

  return (
    <Box>
      {!isExternal && (
        <IconButton sx={{ color: 'text.primary' }} onClick={handleClick}>
          <Badge badgeContent={read} color="primary">
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      )}
      <Popover
        open={open}
        anchorEl={resolvedAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {open && (
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <Card elevation={0}>
                <CardHeader
                  title="Notificaciones"
                  slotProps={{
                    title: { variant: 'subtitle1' },
                  }}
                />
                <Stack direction="row" spacing={2} sx={{ px: 2, pb: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DoneAllIcon />}
                    disabled={alerts?.filter((a) => a.readAt === null).length === 0}
                    onClick={() => {
                      readAlerts({ alertIds: alerts.map((a) => a.id), readAlert: true })
                        .then(() => {
                          refetchAlerts().catch((e) => console.error(e));
                        })
                        .catch((e) => console.error(e));
                    }}
                  >
                    Marcar todo como leído
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ArchiveIcon />}
                    disabled={alerts?.length === 0}
                    onClick={() => {
                      archiveAlerts({ alertIds: alerts.map((a) => a.id), archiveAlert: true })
                        .then(() => {
                          refetchAlerts().catch((e) => console.error(e));
                        })
                        .catch((e) => console.error(e));
                    }}
                  >
                    Archivar todo
                  </Button>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <List
                  dense
                  sx={{
                    px: 0,
                    py: 0.5,
                    width: '100%',
                    '& .MuiListItemButton-root': {
                      minHeight: 36,
                      py: 0.5,
                      px: 1,
                      gap: 0.5,
                      width: '100%',
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiListItemIcon-root': {
                      minWidth: '10px',
                      mr: 0.5,
                    },
                    '& .MuiListItemText-root': {
                      minWidth: 0,
                      flex: 1,
                      width: '100%',
                    },
                  }}
                >
                  {alertsByBusinessUnit &&
                    Object.keys(alertsByBusinessUnit)
                      .sort((a, b) => a.localeCompare(b))
                      .map((businessUnit) => {
                        return (
                          <div key={businessUnit}>
                            {Object.keys(alertsByBusinessUnit).length > 1 && (
                              <ListSubheader
                                key={businessUnit}
                                sx={{ textAlign: 'center', backgroundColor: '#f0f0f0' }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                  }}
                                >
                                  {businessUnit}
                                </Typography>
                              </ListSubheader>
                            )}
                            {alertsByBusinessUnit[businessUnit].map((alert) => (
                              <Box key={alert.id}>
                                <ListItemButton
                                  selected={alert.readAt === null}
                                  onClick={() => {
                                    if (alert.readAt === null) {
                                      readAlerts({ alertIds: [alert.id], readAlert: true })
                                        .then(() => {
                                          refetchAlerts()
                                            .then(() => {
                                              if (alert.redirectPath) {
                                                navigate(alert.redirectPath);
                                              }
                                            })
                                            .catch((e) => console.error(e));
                                        })
                                        .catch((e) => console.error(e));
                                    } else if (alert.redirectPath) {
                                      navigate(alert.redirectPath);
                                    }
                                  }}
                                >
                                  {alert.readAt === null && (
                                    <ListItemIcon sx={{ minWidth: '10px', mr: 0.5 }}>
                                      <CircleIcon sx={{ fontSize: '10px' }} color="primary" />
                                    </ListItemIcon>
                                  )}
                                  <ListItemText
                                    primary={
                                      <Typography variant="body1" noWrap>
                                        {alert.identifier}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{
                                          color: 'text.secondary',
                                        }}
                                      >
                                        {new Date(alert.createdAt).toLocaleTimeString('es-CL', {
                                          day: 'numeric',
                                          month: 'numeric',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          hour12: false,
                                        })}
                                      </Typography>
                                    }
                                    sx={{ minWidth: 0, flex: 1, width: '100%' }}
                                  />
                                  <ListItemIcon sx={{ minWidth: '10px', mr: 0.5, gap: 0.5 }}>
                                    <Tooltip title="Archivar" placement="right">
                                      <IconButton
                                        edge="end"
                                        aria-label="archivar"
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          archiveAlerts({ alertIds: [alert.id], archiveAlert: true })
                                            .then(() => {
                                              refetchAlerts().catch((e) => console.error(e));
                                            })
                                            .catch((e) => console.error(e));
                                        }}
                                      >
                                        <InventoryIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {alert.readAt !== null && (
                                      <Tooltip title="Marcar como NO leído" placement="right">
                                        <IconButton
                                          edge="end"
                                          aria-label="marcar como no leído"
                                          size="small"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            readAlerts({ alertIds: [alert.id], readAlert: false })
                                              .then(() => {
                                                refetchAlerts().catch((e) => console.error(e));
                                              })
                                              .catch((e) => console.error(e));
                                          }}
                                        >
                                          <MarkChatUnread fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </ListItemIcon>
                                </ListItemButton>
                                <Divider />
                              </Box>
                            ))}
                          </div>
                        );
                      })}
                  {alerts?.length === 0 && (
                    <ListItemButton>
                      <ListItemText primary="No hay notificaciones" />
                    </ListItemButton>
                  )}
                </List>
              </Card>
            </ClickAwayListener>
          </Paper>
        )}
      </Popover>
    </Box>
  );
};

export default Alerts;
